import { sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
/**
 * Fetches statistical data about raw records
 * @param {object} params - Parameters for the request
 * @param {string} params.clientId - Client ID
 * @param {string} params.usecaseId - LLM usecase ID
 * @returns {Promise} - A promise that resolves to the stats data
 */
export const fetchRawRecordsStats = (params) => {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            "raw-records-stats",
            {
                client_id: params.clientId,
                llm_usecase_id: params.usecaseId,
            },
            (response) => {
                resolve(response);
            },
            (error) => {
                console.error("Failed to fetch statistics:", error);
                reject(error);
            }
        );
    });
};

/**
 * Fetches response options for the dropdown
 * @param {object} params - Parameters for the request
 * @param {string} params.clientId - Client ID
 * @param {string} params.usecaseId - LLM usecase ID
 * @returns {Promise} - A promise that resolves to an array of response options
 */
export const fetchResponseOptions = (params) => {
    return new Promise((resolve, reject) => {

    });
};

/**
 * Fetches raw records data based on filters and pagination
 * @param {object} params - Parameters for the request
 * @param {string} params.clientId - Client ID
 * @param {string} params.usecaseId - LLM usecase ID
 * @param {number} params.page - Page number
 * @param {number} params.pageSize - Page size
 * @param {object} params.filters - Filter options
 * @param {string} params.filters.exampleStatus - Example status filter
 * @param {string} params.filters.responseStatus - Response status filter
 * @param {string} params.filters.searchQuery - Search query for ID
 * @param {string} params.filters.rawPromptQuery - Text to search in prompts
 * @param {string} params.filters.rawResponseQuery - Text to search in responses
 * @returns {Promise} - A promise that resolves to the records data
 */
export const fetchRawRecords = (params) => {
    // Create a clean filters object with only non-empty values
    const cleanFilters = {};

    // Only include filters with actual values (not undefined, null, or empty strings)
    if (params.filters.exampleStatus) {
        cleanFilters.example_status = params.filters.exampleStatus;
    }

    if (params.filters.responseStatus) {
        cleanFilters.response_status = params.filters.responseStatus;
    }

    if (params.filters.searchQuery && params.filters.searchQuery.trim()) {
        cleanFilters.search_query = params.filters.searchQuery.trim();
    }

    if (params.filters.rawPromptQuery && params.filters.rawPromptQuery.trim()) {
        cleanFilters.raw_prompt_query = params.filters.rawPromptQuery.trim();
    }

    if (params.filters.rawResponseQuery) {
        cleanFilters.raw_response_query = params.filters.rawResponseQuery;
    }

    console.log("Sending filters to server:", cleanFilters);

    const requestBody = {
        client_id: params.clientId,
        llm_usecase_id: params.usecaseId,
        page: params.page,
        page_size: params.pageSize
    };

    // Only include filters if there are any
    if (Object.keys(cleanFilters).length > 0) {
        requestBody.filters = cleanFilters;
    }

    console.log("Full request body:", requestBody);

    return new Promise((resolve, reject) => {
        data_from_server_post(
            "raw-records",
            requestBody,
            (response) => {
                console.log("Received response:", response);
                resolve(response);
            },
            (error) => {
                console.error("Failed to fetch records:", error);
                reject(error);
            }
        );
    });
};

/**
 * Fetches a raw example record
 * @param {object} params - Parameters for the request
 * @param {string} params.clientId - Client ID
 * @param {string} params.usecaseId - LLM usecase ID
 * @param {string} params.recordId - Record ID
 * @returns {Promise} - A promise that resolves to the raw example data
 */
export const fetchRawExample = (params) => {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            "raw-record-example",
            {
                client_id: params.clientId,
                llm_usecase_id: params.usecaseId,
                record_id: params.recordId
            },
            (response) => {
                resolve(response);
            },
            (error) => {
                console.error("Failed to fetch raw example:", error);
                reject(error);
            }
        );
    });
};

/**
 * Edits a raw record and optionally reprocesses it
 * @param {object} params - Parameters for the request
 * @param {string} params.clientId - Client ID
 * @param {string} params.usecaseId - LLM usecase ID
 * @param {string} params.recordId - Record ID
 * @param {string} [params.rawPrompt] - Updated raw prompt text (optional)
 * @param {string} [params.rawResponse] - Updated raw response text (optional)
 * @param {boolean} [params.reprocessExample=false] - Whether to reprocess the example
 * @param {boolean} [params.reprocessResponse=false] - Whether to reprocess the response
 * @returns {Promise} - A promise that resolves to the updated record data
 */
export const editRawRecord = (params) => {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            "edit-raw-record",
            {
                client_id: params.clientId,
                llm_usecase_id: params.usecaseId,
                record_id: params.recordId,
                raw_prompt: params.rawPrompt,
                raw_response: params.rawResponse,
                reprocess_example: params.reprocessExample || false,
                reprocess_response: params.reprocessResponse || false
            },
            (response) => {
                resolve(response);
            },
            (error) => {
                console.error("Failed to edit raw record:", error);
                reject(error);
            }
        );
    });
}; 